import { COMPANY_FIELDS, IDENTITY, OPTIONAL_FIELDS, USER_FIELDS, getServerError } from './fields'
import { finalExp, CARD_TYPE, isValidPesel } from 'utils/validation'
import { selectForm, updateValue, updateValues } from '../formSlice'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from 'hooks/useTranslation'
import InputWithOptions from './InputWithOptions'
import Select from 'components/inputs/Select'
import Submit from 'components/inputs/Submit'
import Input from 'components/inputs/Input'
import { useState } from 'react'

const PESEL_OPTION = ''

const toPostalCode = value => value.replace(/^(\d{2})-?(\d{3})$/, '$1-$2')

const Form = ({ block, sections, validation, isPolish, isCompanyPolish, submit, showIdentificationPopup, secondaryTheme = false, isLoading = false }) => {
  const form = useSelector(selectForm)
  const [warning, setWarning] = useState({})
  const [touched, setTouched] = useState({})

  const dispatch = useDispatch()
  const { t } = useTranslation('form')

  const { identity, documentTypes, userNames, userAddress, userIdentity, companyFields } = sections
  const userBlock = validation?.user
  const companyBlock = validation?.company

  const value = key => form[key] || ''
  const cardType = form.cardType ?? (identity === IDENTITY.peselOrDocument ? PESEL_OPTION : (documentTypes[0] || CARD_TYPE.idCard))
  const choosePesel = identity === IDENTITY.peselOrDocument && cardType === PESEL_OPTION
  const showPesel = userIdentity && identity !== IDENTITY.document && (identity !== IDENTITY.peselOrDocument || choosePesel)
  const showDocument = userIdentity && identity !== IDENTITY.pesel && !choosePesel
  const identityTouched = touched.pesel || touched.cardType || touched.identityCard

  const refs = []


  const serverWarning = (block, apiKey, formKey) => {
    if (touched[formKey]) return false
    const error = getServerError(block, apiKey)
    if (!error || error.code === 'one_of_required') return false
    return error.message || t('wrongNumber')
  }

  const userWarning = apiKey => {
    const formKey = USER_FIELDS[apiKey]
    return warning[formKey] || serverWarning(userBlock, apiKey, formKey) || undefined
  }

  const companyWarning = apiKey => {
    const formKey = COMPANY_FIELDS[apiKey]
    return warning[formKey] || serverWarning(companyBlock, apiKey, formKey) || undefined
  }

  const oneOfMessage = !identityTouched && ['pesel', 'identity_card', 'identity_card_type']
    .map(f => getServerError(userBlock, f))
    .find(e => e?.code === 'one_of_required')?.message

  const markTouched = name => {
    if (!touched[name]) setTouched(prev => ({ ...prev, [name]: true }))
    if (warning[name]) setWarning(prev => ({ ...prev, [name]: false }))
  }

  const changeHandler = e => {
    if (!e) return
    const { name, value } = e.target
    if (name === 'postalCode' && !isPolish) {
      // kod pocztowy spoza PL — bez maski NN-NNN
      dispatch(updateValues({ [name]: value }))
    } else {
      dispatch(updateValue({ name, value }))
    }
    markTouched(name)
  }

  const blurHandler = e => {
    if (e?.target?.name && e.type !== 'blur') markTouched(e.target.name)
  }

  const setCardType = type => {
    dispatch(updateValues({ cardType: type, identityCard: '' }))
    markTouched('cardType')
    if (warning.identityCard) setWarning(prev => ({ ...prev, identityCard: false }))
  }

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !block) validate()
  }

  const checkField = (formKey, required) => {
    const v = value(formKey).trim()
    if (!v) return required ? t('requiredField') : false

    switch (formKey) {
      case 'postalCode':
        return isPolish && !v.match(finalExp.postalCode) ? '**-***' : false
      case 'companyPostalCode':
        return isCompanyPolish && !v.match(finalExp.postalCode) ? '**-***' : false
      case 'pesel':
        return !isValidPesel(v.replace(/[\s-]/g, '')) ? t('incorrectPESEL') : false
      case 'identityCard': {
        const exp = finalExp[cardType]
        return exp && !v.match(exp) ? t('wrongNumber') : false
      }
      case 'companyNip': {
        const nip = v.replace(/[\s-]/g, '')
        return /^[a-z]{2}/i.test(nip) && !/^pl/i.test(nip)
          ? false
          : !nip.replace(/^pl/i, '').match(finalExp.nip) ? t('incorrectTIN') : false
      }
      default:
        return false
    }
  }

  const validate = () => {
    const newWarning = {}
    const customer = {}
    const company = {}

    userNames.forEach(apiKey => {
      const formKey = USER_FIELDS[apiKey]
      newWarning[formKey] = checkField(formKey, true)
      customer[apiKey] = value(formKey).trim()
    })

    if (userAddress) {
      ['street', 'building_nr', 'apartment_nr', 'zip_code', 'city'].forEach(apiKey => {
        const formKey = USER_FIELDS[apiKey]
        newWarning[formKey] = checkField(formKey, !OPTIONAL_FIELDS.includes(apiKey))
        customer[apiKey] = apiKey === 'zip_code' && isPolish
          ? toPostalCode(value(formKey).trim())
          : value(formKey).trim()
      })
    }

    if (userIdentity) {
      const peselRequired = identity === IDENTITY.pesel || identity === IDENTITY.peselAndDocument || choosePesel
      const documentRequired = identity === IDENTITY.document || identity === IDENTITY.peselAndDocument || (identity === IDENTITY.peselOrDocument && !choosePesel)

      if (showPesel) {
        newWarning.pesel = checkField('pesel', peselRequired)
        if (value('pesel') || peselRequired) {
          customer.pesel = value('pesel').replace(/[\s-]/g, '')
        }
      }

      if (showDocument) {
        newWarning.identityCard = checkField('identityCard', documentRequired)
        if (!cardType && value('identityCard')) newWarning.cardType = t('selectDocumentType')
        if (value('identityCard') || documentRequired) {
          customer.identity_card_type = cardType
          customer.identity_card = value('identityCard').trim()
        }
      }
    }

    companyFields.forEach(apiKey => {
      const formKey = COMPANY_FIELDS[apiKey]
      newWarning[formKey] = checkField(formKey, !OPTIONAL_FIELDS.includes(apiKey))
      company[apiKey] = apiKey === 'postalCode' && isCompanyPolish
        ? toPostalCode(value(formKey).trim())
        : value(formKey).trim()
    })

    if (Object.values(newWarning).some(Boolean)) {
      setWarning(newWarning)
      return
    }

    setWarning({})
    setTouched({})
    submit({
      customer: Object.keys(customer).length > 0 ? customer : undefined,
      company: Object.keys(company).length > 0 ? company : undefined,
    })
  }

  const documentLabel = type => t(type === CARD_TYPE.idCard ? 'idNumber' : type === CARD_TYPE.passport ? 'passportNumber' : 'drivingLicenseNumber')
  const documentPlaceholder = type => t(type === CARD_TYPE.idCard ? 'enterIdNumber' : type === CARD_TYPE.passport ? 'enterPassportNumber' : 'enterDrivingLicenseNumber')
  const documentOptions = documentTypes.map(v => ({ value: v, name: documentLabel(v) }))

  const showUserSection = userNames.length > 0 || userAddress || userIdentity

  return (
    <div className='contract__form form'>
      {showUserSection && <>
        {companyFields.length > 0 && <div className='contract__form__header'>{t('personalData')}</div>}

        {userNames.includes('first_name') && <Input
          required
          ref={r => refs.push(r)}
          value={value('firstName')}
          name='firstName'
          label={t('firstName')}
          maxLength='100'
          autoComplete='given-name'
          disabled={block}
          warning={userWarning('first_name')}
          placeholder={t('enterFirstName')}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />}

        {userNames.includes('surname') && <Input
          required
          ref={r => refs.push(r)}
          value={value('lastName')}
          name='lastName'
          label={t('lastName')}
          maxLength='100'
          autoComplete='family-name'
          disabled={block}
          warning={userWarning('surname')}
          placeholder={t('enterLastName')}
          enterClicked={enterClicked}
          changeHandler={changeHandler} />}

        {userAddress && <>
          <InputWithOptions
            required
            secondaryTheme={secondaryTheme}
            ref={r => refs.push(r)}
            value={value('city')}
            name='city'
            label={t('city')}
            maxLength='200'
            disabled={block}
            warning={userWarning('city')}
            autoComplete='address-level2'
            placeholder={t('enterCity')}
            enterClicked={enterClicked}
            blurHandler={blurHandler}
            changeHandler={changeHandler} />

          <InputWithOptions
            required
            secondaryTheme={secondaryTheme}
            ref={r => refs.push(r)}
            value={value('street')}
            name='street'
            label={t('street')}
            maxLength='200'
            disabled={block}
            additionalValues={{ city: value('city') }}
            autoComplete='street-address'
            warning={userWarning('street')}
            placeholder={t('enterStreet')}
            enterClicked={enterClicked}
            blurHandler={blurHandler}
            changeHandler={changeHandler} />

          <InputWithOptions
            required
            secondaryTheme={secondaryTheme}
            ref={r => refs.push(r)}
            value={value('houseNumber')}
            name='houseNumber'
            label={t('houseNumber')}
            maxLength='10'
            disabled={block}
            additionalValues={value('city').length > 1 && value('street').length > 1 ? { city: value('city'), cityDistricted: form.cityDistricted || value('city'), street: value('street'), houseNumber: value('houseNumber') } : false}
            warning={userWarning('building_nr')}
            placeholder={t('enterHouseNumber')}
            blurHandler={blurHandler}
            enterClicked={enterClicked}
            changeHandler={changeHandler} />

          <Input
            ref={r => refs.push(r)}
            value={value('localNumber')}
            name='localNumber'
            label={t('apartmentNumber')}
            maxLength='10'
            disabled={block}
            warning={userWarning('apartment_nr')}
            placeholder={t('enterApartmentNumber')}
            enterClicked={enterClicked}
            blurHandler={blurHandler}
            changeHandler={changeHandler} />

          <InputWithOptions
            required
            secondaryTheme={secondaryTheme}
            ref={r => refs.push(r)}
            value={value('postalCode')}
            name='postalCode'
            label={t('postalCode')}
            maxLength={isPolish ? '6' : '12'}
            disabled={block}
            autoComplete='postal-code'
            additionalValues={isPolish && value('city').length > 1 && value('street').length > 1 ? { city: value('city'), street: value('street'), houseNumber: value('houseNumber'), streetPrefix: form.streetPrefix } : false}
            warning={userWarning('zip_code')}
            placeholder={t('enterPostalCode')}
            enterClicked={enterClicked}
            blurHandler={blurHandler}
            changeHandler={e => {
              if (isPolish) e.target.value = e.target.value.replace(/^(\d{2})(\d+)$/, '$1-$2')
              changeHandler(e)
            }} />
        </>}

        {userIdentity && <>
          {showIdentificationPopup && <div
            className='form__info'
            onClick={showIdentificationPopup}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18.5" height="18.5" viewBox="0 0 18.5 18.5">
              <g transform="translate(0.75 0.75)">
                <circle cx="8.5" cy="8.5" r="8.5" fill="none" stroke="var(--theme-text)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                <path d="M9.09,8.691a2.538,2.538,0,0,1,4.931.846c0,1.692-2.538,2.538-2.538,2.538" transform="translate(-3.093 -2.77)" fill="none" stroke="var(--theme-text)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                <line transform="translate(8 13)" fill="none" stroke="var(--theme-text)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
              </g>
            </svg>

            <span>{t('whyDoWeNeedYourData')}</span>
          </div>}

          {oneOfMessage && <div className='contract__form__warning'>{oneOfMessage}</div>}

          {identity === IDENTITY.peselOrDocument && <Select
            label={t('documentType')}
            value={cardType}
            name='cardType'
            disabled={block}
            setValue={value => {
              setCardType(value)
              if (value) dispatch(updateValue({ name: 'pesel', value: '' }))
            }}
            options={[{ value: PESEL_OPTION, name: t('pesel') }, ...documentOptions]} />}

          {showPesel && <Input
            required={identity !== IDENTITY.none}
            ref={r => refs.push(r)}
            value={value('pesel')}
            name='pesel'
            type='password'
            label={t('pesel')}
            minLength='11'
            maxLength='11'
            disabled={block}
            warning={warning.pesel || serverWarning(userBlock, 'pesel', 'pesel') || undefined}
            placeholder={t('enterPESEL')}
            enterClicked={enterClicked}
            changeHandler={changeHandler} />}

          {showDocument && <>
            {identity !== IDENTITY.peselOrDocument && <Select
              label={t('documentType')}
              value={cardType}
              name='cardType'
              disabled={block}
              warning={warning.cardType || serverWarning(userBlock, 'identity_card_type', 'cardType') || undefined}
              setValue={setCardType}
              options={documentOptions} />}

            <Input
              required={identity !== IDENTITY.none}
              ref={r => refs.push(r)}
              value={value('identityCard')}
              name='identityCard'
              type='password'
              label={documentLabel(cardType)}
              disabled={block}
              warning={userWarning('identity_card')}
              placeholder={documentPlaceholder(cardType)}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />
          </>}
        </>}
      </>}

      {companyFields.length > 0 && <>
        <div className='contract__form__header'>{t('companyDetails')}</div>

        {[
          ['name', 'name', 'enterCompanyName', '300'],
          ['nip', 'tin', 'enterTIN', '20'],
          ['streetName', 'street', 'enterStreet', '200'],
          ['homeNr', 'houseNumber', 'enterHouseNumber', '10'],
          ['flatNr', 'apartmentNumber', 'enterApartmentNumber', '10'],
          ['postalCode', 'postalCode', 'enterPostalCode', isCompanyPolish ? '6' : '12'],
          ['cityName', 'city', 'enterCity', '200'],
        ]
          .filter(([apiKey]) => companyFields.includes(apiKey))
          .map(([apiKey, label, placeholder, maxLength]) => <Input
            key={apiKey}
            required={!OPTIONAL_FIELDS.includes(apiKey)}
            ref={r => refs.push(r)}
            value={value(COMPANY_FIELDS[apiKey])}
            name={COMPANY_FIELDS[apiKey]}
            label={t(label)}
            maxLength={maxLength}
            disabled={block}
            warning={companyWarning(apiKey)}
            placeholder={t(placeholder)}
            enterClicked={enterClicked}
            changeHandler={e => {
              if (apiKey === 'postalCode' && isCompanyPolish) {
                e.target.value = e.target.value.replace(/^(\d{2})(\d+)$/, '$1-$2')
              }
              changeHandler(e)
            }} />)}
      </>}

      <Submit
        text={t('save')}
        isLoading={isLoading}
        action={() => !block && validate()} />
    </div>
  )
}

export default Form
